/**
 * DDD Analysis Tools - Pure Vercel AI Tools
 * 
 * These are pure function tools with no dependencies, following the same
 * pattern as file tools and feature tools. They can be used anywhere
 * without creating cross-domain dependencies.
 * 
 * Eric Evans Pattern: Tools as Published Language
 */

import { tool } from 'ai';
import { z } from 'zod';
import * as path from 'path';
import * as fs from 'fs/promises';
import { glob } from 'glob';

/**
 * Pure DDD Analysis Tools - No Dependencies
 */
export const dddAnalysisTools: Record<string, any> = {
  /**
   * Explore domain package structure
   */
  exploreStructure: tool({
    description: 'Explore domain package structure to understand organization and find key files',
    parameters: z.object({
      domainPath: z.string().describe('Path to the domain package directory')
    }),
    execute: async ({ domainPath }) => {
      try {
        const patterns = {
          aggregates: `${domainPath}/**/aggregates/**/*.ts`,
          entities: `${domainPath}/**/entities/**/*.ts`,
          valueObjects: `${domainPath}/**/value-objects/**/*.ts`,
          events: `${domainPath}/**/events/**/*.ts`,
          commands: `${domainPath}/**/commands/**/*.ts`,
          queries: `${domainPath}/**/queries/**/*.ts`,
          repositories: `${domainPath}/**/repositories/**/*.ts`,
          services: `${domainPath}/**/services/**/*.ts`
        };

        const structure: Record<string, string[]> = {};
        
        for (const [key, pattern] of Object.entries(patterns)) {
          structure[key] = await glob(pattern);
        }

        // Check for published language
        const hasPublishedLanguage = await fs.access(
          path.join(domainPath, 'integration', 'contracts', 'index.ts')
        ).then(() => true).catch(() => false);

        return {
          domainPath,
          structure,
          hasPublishedLanguage,
          summary: {
            aggregates: structure.aggregates?.length || 0,
            entities: structure.entities?.length || 0,
            valueObjects: structure.valueObjects?.length || 0,
            events: structure.events?.length || 0,
            services: structure.services?.length || 0
          }
        };
      } catch (error) {
        throw new Error(`Failed to explore structure: ${error}`);
      }
    }
  }),

  /**
   * Find DDD violations using simple file analysis
   */
  findViolations: tool({
    description: 'Find DDD violations by analyzing imports and file structure',
    parameters: z.object({
      domainPath: z.string().describe('Path to the domain package directory'),
      violationType: z.enum(['direct-imports', 'improper-layering', 'aggregate-boundaries'])
    }),
    execute: async ({ domainPath, violationType }) => {
      const violations: Array<{
        file: string;
        type: string;
        message: string;
        line?: number;
      }> = [];

      try {
        // Get all TypeScript files
        const files = await glob(`${domainPath}/**/*.ts`, {
          ignore: ['**/node_modules/**', '**/dist/**', '**/*.test.ts', '**/*.spec.ts']
        });

        for (const file of files) {
          const content = await fs.readFile(file, 'utf-8');
          const lines = content.split('\n');

          if (violationType === 'direct-imports') {
            // Check for direct imports from other domains
            lines.forEach((line, index) => {
              if (line.includes('import') && line.includes('from')) {
                // Check for cross-domain imports
                const importMatch = line.match(/from\s+['"](@swoft\/[^'"]+)['"]/);
                if (importMatch) {
                  const importedPackage = importMatch[1];
                  // Skip allowed imports (core packages)
                  const allowedPackages = [
                    '@swoft/core-application',
                    '@swoft/core-architecture',
                    '@swoft/core-errors',
                    '@swoft/core-infrastructure'
                  ];
                  
                  if (!allowedPackages.includes(importedPackage) && 
                      !file.includes('/interface/') && // Interface layer can import
                      !file.includes('/infrastructure/adapters/')) { // Adapters can import
                    violations.push({
                      file,
                      type: 'direct-import',
                      message: `Direct import from ${importedPackage} violates DDD boundaries`,
                      line: index + 1
                    });
                  }
                }
              }
            });
          }

          if (violationType === 'improper-layering') {
            // Check for layering violations
            const filePath = path.relative(domainPath, file);
            
            // Domain layer shouldn't import from application or infrastructure
            if (filePath.includes('/domain/')) {
              lines.forEach((line, index) => {
                if (line.includes('import') && 
                    (line.includes('/application/') || 
                     line.includes('/infrastructure/') ||
                     line.includes('/interface/'))) {
                  violations.push({
                    file,
                    type: 'improper-layering',
                    message: 'Domain layer importing from outer layers',
                    line: index + 1
                  });
                }
              });
            }
          }

          if (violationType === 'aggregate-boundaries') {
            // Check for aggregate boundary violations
            if (file.includes('/aggregates/')) {
              lines.forEach((line, index) => {
                // Check if aggregate is importing from another aggregate
                if (line.includes('import') && 
                    line.includes('/aggregates/') &&
                    !line.includes(path.dirname(file))) {
                  violations.push({
                    file,
                    type: 'aggregate-boundary',
                    message: 'Aggregate importing from another aggregate',
                    line: index + 1
                  });
                }
              });
            }
          }
        }

        return {
          domainPath,
          violationType,
          violations,
          summary: `Found ${violations.length} ${violationType} violations`
        };
      } catch (error) {
        throw new Error(`Failed to find violations: ${error}`);
      }
    }
  }),

  /**
   * Generate DDD health report
   */
  generateHealthReport: tool({
    description: 'Generate a comprehensive DDD health report for a domain',
    parameters: z.object({
      domainPath: z.string().describe('Path to the domain package directory')
    }),
    execute: async ({ domainPath }): Promise<any> => {
      try {
        // Get structure
        const structureResult: any = await dddAnalysisTools.exploreStructure.execute({ domainPath });
        
        // Check for violations
        const violationTypes = ['direct-imports', 'improper-layering', 'aggregate-boundaries'] as const;
        const allViolations: any[] = [];
        
        for (const type of violationTypes) {
          const result = await dddAnalysisTools.findViolations.execute({ 
            domainPath, 
            violationType: type 
          });
          allViolations.push(...result.violations);
        }

        // Calculate health score
        const totalFiles = Object.values(structureResult.structure)
          .flat()
          .filter((v, i, a) => a.indexOf(v) === i)
          .length;
        
        const healthScore = Math.max(0, 100 - (allViolations.length * 5));
        
        return {
          domainPath,
          healthScore,
          structure: structureResult.summary,
          violations: {
            total: allViolations.length,
            byType: {
              'direct-imports': allViolations.filter(v => v.type === 'direct-import').length,
              'improper-layering': allViolations.filter(v => v.type === 'improper-layering').length,
              'aggregate-boundaries': allViolations.filter(v => v.type === 'aggregate-boundary').length
            }
          },
          hasPublishedLanguage: structureResult.hasPublishedLanguage,
          recommendation: healthScore >= 80 ? 'Healthy' : 
                         healthScore >= 60 ? 'Needs Attention' : 
                         'Critical Issues'
        };
      } catch (error) {
        throw new Error(`Failed to generate health report: ${error}`);
      }
    }
  })
};

/**
 * Export individual tools for flexibility
 */
export const exploreStructureTool = dddAnalysisTools.exploreStructure;
export const findViolationsTool = dddAnalysisTools.findViolations;
export const generateHealthReportTool = dddAnalysisTools.generateHealthReport;